import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

interface GoogleAnalyticsInjectorProps {
  measurementId?: string | null;
}

/**
 * Measurement ids que já receberam `config` nesta carga de página. Precisa
 * viver fora do React: o componente remonta a cada troca de rota (o
 * PublicPageView é recriado), então um useRef zeraria aqui e faríamos
 * `config` de novo — que o GA4 trata como duplicado e ignora, sem mandar
 * page_view nenhum. Escopo de módulo tem exatamente o mesmo tempo de vida
 * do estado do gtag no `window`.
 */
const configuredMeasurementIds = new Set<string>();

function consentKey() {
  return `lumenise-cms-consent:${window.location.hostname.toLowerCase()}`;
}

function hasAnalyticsConsent() {
  return typeof window !== 'undefined' && window.localStorage.getItem(consentKey()) === 'accepted';
}

export function GoogleAnalyticsInjector({ measurementId }: GoogleAnalyticsInjectorProps) {
  const [consented, setConsented] = useState(hasAnalyticsConsent);
  const location = useLocation();

  useEffect(() => {
    const updateConsent = () => setConsented(hasAnalyticsConsent());
    window.addEventListener('storage', updateConsent);
    window.addEventListener('lumenise:analytics-consent', updateConsent);
    return () => {
      window.removeEventListener('storage', updateConsent);
      window.removeEventListener('lumenise:analytics-consent', updateConsent);
    };
  }, []);

  useEffect(() => {
    const id = measurementId?.trim();
    if (!id || !consented) return;

    const scriptId = `lumenise-ga4-${id}`;
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
      document.head.appendChild(script);
    }

    window.dataLayer = window.dataLayer ?? [];
    // Precisa empurrar o objeto `arguments`, não um array. O gtag.js só
    // reconhece comandos que chegam como Arguments — um Array (ex.: via
    // rest `...args`) é ignorado silenciosamente: o script carrega, o
    // dataLayer enche, `google_tag_data` inicializa, e mesmo assim nenhum
    // hit sai pro /g/collect. Por isso a função é `function` (tem
    // `arguments` próprio) e não arrow.
    if (!window.gtag) {
      window.gtag = function gtag() {
        // O gtag.js exige o objeto Arguments; trocar por rest params quebra
        // a coleta silenciosamente — era exatamente esse o bug.
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer?.push(arguments);
      };
    }

    const pagePath = location.pathname + location.search;

    // Primeira configuração desta measurement id: o `config` já dispara o
    // page_view inicial, com o título correto que veio do HTML do Worker.
    if (!configuredMeasurementIds.has(id)) {
      configuredMeasurementIds.add(id);
      window.gtag('js', new Date());
      window.gtag('config', id, {
        send_page_view: true,
        page_path: pagePath,
        page_title: document.title,
      });
      return;
    }

    // Navegação SPA: o `config` não roda de novo, então o page_view precisa
    // ser mandado na mão — sem isso o GA só registraria a primeira página
    // de cada sessão. Adiado um tick porque PublicSiteMeta (que atualiza o
    // document.title) roda depois deste efeito na árvore; sem o adiamento o
    // hit iria com o título da página anterior.
    const timer = window.setTimeout(() => {
      window.gtag?.('event', 'page_view', {
        page_path: pagePath,
        page_title: document.title,
        page_location: window.location.href,
      });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [consented, measurementId, location.pathname, location.search]);

  return null;
}
